import * as Yup from "yup";
import { IFundingRequestForm } from "models/HaulPay";
import { IOption } from "models/Form";
import { formatNumber } from "helpers/formatNumber";
import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";

export const fundingRequestFormSchema = (
  isHideDebtor: boolean,
  selfFinanceLimit: number,
  allowSelfFinanceFr: boolean,
  selfFinanceFrRemainingBalance: number,
  allowFrWithoutCarrier: boolean,
  allowNegativeMargin: boolean
) =>
  Yup.object<Partial<IFundingRequestForm>>({
    lineItems: Yup.array().of(
      Yup.object().shape({
        amount: Yup.number()
          .required("This field is required.")
          .typeError("This field is required.")
      })
    ),
    amount: Yup.number()
      .required("This field is required")
      .typeError("This field is required")
      .test("shipper-invoice-amount", "", function (value) {
        const { contractType } = this.parent as IFundingRequestForm;

        // let required/typeError handle missing or non-numeric
        if (typeof value !== "number") return true;

        const isSelfFinance = contractType.value.startsWith("SELF_FINANCE");

        if (allowSelfFinanceFr && isSelfFinance) {
          // self-finance: enforce [paymentProfileAmount … limit]
          const limit = Math.min(selfFinanceFrRemainingBalance, selfFinanceLimit);

          // If both values are 0 or not positive, set a reasonable minimum limit
          const effectiveLimit = limit <= 0 ? 1 : limit;

          // if (value < paymentProfileAmount) {
          //   return this.createError({
          //     message: `must be greater or equal to ${formatNumber(paymentProfileAmount, 2)}`
          //   });
          // }
          if (value > effectiveLimit) {
            return this.createError({
              message: `Self finance funding request cannot be more than ${formatNumber(effectiveLimit, 2)}`
            });
          }
        } else {
          // normal factored: enforce ≥ $1
          if (value < 1) {
            return this.createError({
              message: "must be greater or equal to $1"
            });
          }
        }

        return true;
      })
      // keep your “blank-string → undefined” transform if you like:
      .transform((val, orig) => (orig === "" ? undefined : val)),
    debtor: Yup.object().when("contractType", {
      is: (contractType: IOption) =>
        isHideDebtor ||
        contractType.value === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.required("This field is required")
    }),
    paymentProfileAmount: Yup.number().when("contractType", {
      is: (ct: IOption) =>
        // only validate when it's NOT Non-Factored Bill-Out
        ct?.value !== FUNDING_REQUEST_CONTRACT_TYPES.NON_FACTORED_BILL_OUT,
      then: (schema) =>
        schema
          .required("This field is required")
          .typeError("Carrier amount must be greater than $1")
          .test(
            "carrier-amount",
            "", // we’ll supply custom messages via createError()
            function (value) {
              const { contractType, amount } = this.parent as IFundingRequestForm;

              // let the required/typeError handle missing or non-numeric
              if (typeof value !== "number") return true;

              const isSelfFinance = contractType.value.startsWith("SELF_FINANCE");
              const isFactored = !contractType.value.startsWith("NON_FACTORED");
              const isOffPlatform =
                contractType.value ===
                FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM;
              const floor = 1; // $1 minimum
              const allowNegativeMarginOnSave = allowNegativeMargin && !isSelfFinance;

              if (isOffPlatform && value === 0) return true;

              if (isFactored && value <= 0) {
                return this.createError({
                  message: "Carrier payment must be greater than $0 for factored funding requests."
                });
              }
              // 1) floor check
              if (value <= floor) {
                return this.createError({ message: "must be greater than $1" });
              }
              // 2) ceiling check
              if (value > amount && !isSelfFinance && !allowNegativeMarginOnSave) {
                return this.createError({
                  message: `must be less or equal to ${formatNumber(amount, 2)}`
                });
              }

              // all good
              return true;
            }
          ),
      otherwise: (schema) =>
        // revert blank → undefined so we don't force-validate non-applicable cases
        schema.transform((val, orig) => (orig === "" ? undefined : val))
    }),
    identifyNumber: allowFrWithoutCarrier
      ? Yup.string().nullable()
      : Yup.string().when("contractType", {
          is: (ct: IOption) => ct?.value !== "NON_FACTORED_BILL_OUT",
          then: (schema) => schema.required("This field is required"),
          otherwise: (schema) => schema.nullable()
        }),
    userLoadNumber: Yup.string().required("This field is required")
  });
